import React, {useEffect, useState} from 'react';
import {Document, Page, pdfjs} from 'react-pdf';
import {useNavigate, useParams} from 'react-router-dom';
import {Button, CircularProgress, Typography} from '@mui/material';
import axios from "axios";
import {ReactComponent as ChevronLeftIcon} from "../../assets/icons/chevron-left.svg"
import {useTranslation} from 'react-i18next';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
).toString();

interface FileViewerProps {
    fileId?: string;
    fileUrl?: string;
    fileName?: string;
}

const FileViewer: React.FC<FileViewerProps> = ({fileId, fileUrl, fileName}) => {
    const {t} = useTranslation();
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [fileData, setFileData] = useState<string | null>(null);
    const [numPages, setNumPages] = useState<number | null>(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [error, setError] = useState('');
    const [fileExtension, setFileExtension] = useState<string | null>(null);
    const [textContent, setTextContent] = useState<string | null>(null);
    const [fileType, setFileType] = useState<'pdf' | 'txt' | 'xlsx' | 'docx' | 'pptx' | 'unsupported' | 'loading' | 'error'>('loading');

    useEffect(() => {
        let cancelled = false;

        const loadFile = async () => {
            try {
                setFileData(null);
                setNumPages(null);
                setPageNumber(1);

                const finalFileId = fileId || id;
                let finalFileName = fileName;

                if (!finalFileName && finalFileId) {
                    const metadataResponse = await axios.get(`/documents/${finalFileId}`);
                    finalFileName = metadataResponse.data.name || metadataResponse.data.fileName;
                }

                if (finalFileName) {
                    const extension = finalFileName.split('.').pop()?.toLowerCase() || null;
                    setFileExtension(extension);

                    if (extension === 'txt') {
                        setFileType('txt');
                        const textResponse = await axios.get(fileUrl || `/documents/${finalFileId}/download`, {
                            responseType: 'text'
                        });
                        setTextContent(textResponse.data);
                        return;
                    } else if (extension === 'pdf') {
                        setFileType('pdf');
                    } else if (extension === 'xlsx' || extension === 'xls') {
                        setFileType('xlsx');
                    } else if (extension === 'docx' || extension === 'doc') {
                        setFileType('docx');
                    } else if (extension === 'pptx' || extension === 'ppt') {
                        setFileType('pptx');
                    } else {
                        setFileType('unsupported');
                        return;
                    }
                } else {
                    setFileType('unsupported');
                    return;
                }

                const response = await axios.get(fileUrl || `/documents/${finalFileId}/download`, {
                    responseType: 'blob'
                });
                if (cancelled) return;

                const blob = new Blob([response.data], {type: 'application/pdf'});
                const url = URL.createObjectURL(blob);
                setFileData(url);
            } catch (err) {
                if (!cancelled) {
                    setFileType('error');
                    setError(t('fileViewer.loadError') as string);
                }
                console.error('File load error:', err);
            }
        };

        loadFile();

        return () => {
            cancelled = true;
            if (fileData) URL.revokeObjectURL(fileData);
        };
    }, [fileId, id, fileUrl, fileName]);

    const onDocumentLoadSuccess = ({numPages}: { numPages: number }) => {
        setNumPages(numPages);
    };

    const renderErrorView = () => (
        <div style={{padding: '20px', textAlign: 'center'}}>
            <Typography color="error" variant="h6">{error}</Typography>
            <Button
                variant="contained"
                onClick={() => navigate(-1)}
                sx={{mt: 2}}
            >
                {t('fileViewer.back')}
            </Button>
        </div>
    );

    const renderBackButton = () => (
        <Button
            startIcon={<ChevronLeftIcon/>}
            variant="text"
            onClick={() => navigate(-1)}
        >
            {t('fileViewer.backToList')}
        </Button>
    );

    const renderTextView = () => (
        <div style={{
            padding: '20px',
            maxWidth: '1200px',
            margin: '0 auto'
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px'
            }}>
                {renderBackButton()}
            </div>

            <div style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '20px',
                backgroundColor: '#fff',
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                maxHeight: '80vh',
                overflow: 'auto'
            }}>
                {textContent}
            </div>
        </div>
    );

    const renderXlsxView = () => (
        <div style={{
            padding: '20px',
            maxWidth: '1200px',
            margin: '0 auto'
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px'
            }}>
                {renderBackButton()}
            </div>

            <div style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '40px',
                backgroundColor: '#fff',
                textAlign: 'center'
            }}>
                <Typography variant="h6" color="primary" sx={{mb: 2}}>
                    {t('fileViewer.excelFile')}
                </Typography>
                <Typography variant="body1" sx={{mb: 3}}>
                    {t('fileViewer.excelInfo')}
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => {
                        if (fileData) {
                            const a = document.createElement('a');
                            a.href = fileData;
                            a.download = fileName || `file.${fileExtension || 'xlsx'}`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                        }
                    }}
                    sx={{mt: 2}}
                >
                    {t('fileViewer.download')}
                </Button>
            </div>
        </div>
    );

    const renderDocxView = () => (
        <div style={{
            padding: '20px',
            maxWidth: '1200px',
            margin: '0 auto'
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px'
            }}>
                {renderBackButton()}
            </div>

            <div style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '40px',
                backgroundColor: '#fff',
                textAlign: 'center'
            }}>
                <Typography variant="h6" color="primary" sx={{mb: 2}}>
                    {t('fileViewer.wordFile')}
                </Typography>
                <Typography variant="body1" sx={{mb: 3}}>
                    {t('fileViewer.wordInfo')}
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => {
                        if (fileData) {
                            const a = document.createElement('a');
                            a.href = fileData;
                            a.download = fileName || `file.${fileExtension || 'docx'}`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                        }
                    }}
                    sx={{mt: 2}}
                >
                    {t('fileViewer.download')}
                </Button>
            </div>
        </div>
    );

    const renderPptxView = () => (
        <div style={{
            padding: '20px',
            maxWidth: '1200px',
            margin: '0 auto'
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px'
            }}>
                {renderBackButton()}
            </div>

            <div style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '40px',
                backgroundColor: '#fff',
                textAlign: 'center'
            }}>
                <Typography variant="h6" color="primary" sx={{mb: 2}}>
                    {t('fileViewer.pptFile')}
                </Typography>
                <Typography variant="body1" sx={{mb: 3}}>
                    {t('fileViewer.pptInfo')}
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => {
                        if (fileData) {
                            const a = document.createElement('a');
                            a.href = fileData;
                            a.download = fileName || `file.${fileExtension || 'pptx'}`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                        }
                    }}
                    sx={{mt: 2}}
                >
                    {t('fileViewer.download')}
                </Button>
            </div>
        </div>
    );

    const renderUnsupportedView = () => (
        <div style={{
            padding: '20px',
            maxWidth: '1200px',
            margin: '0 auto',
            textAlign: 'center'
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px'
            }}>
                {renderBackButton()}
            </div>

            <div style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '40px',
                backgroundColor: '#fff'
            }}>
                <Typography variant="h6" color="error">
                    {t('fileViewer.unsupportedFormat')}: .{fileExtension}
                </Typography>
                <Typography variant="body1" style={{marginTop: '16px'}}>
                    {t('fileViewer.supportedFormats')}
                </Typography>
            </div>
        </div>
    );

    const renderPdfView = () => (
        <div style={{
            padding: '20px',
            maxWidth: '1200px',
            margin: '0 auto'
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px'
            }}>
                {renderBackButton()}

                <div style={{display: 'flex', alignItems: 'center', gap: '16px'}}>
                    <Button
                        disabled={pageNumber <= 1}
                        onClick={() => setPageNumber(p => p - 1)}
                        variant="outlined"
                    >
                        {t('fileViewer.previous')}
                    </Button>
                    <Typography>
                        {t('fileViewer.page')} {pageNumber} {t('fileViewer.of')} {numPages}
                    </Typography>
                    <Button
                        disabled={pageNumber >= (numPages || 0)}
                        onClick={() => setPageNumber(p => p + 1)}
                        variant="outlined"
                    >
                        {t('fileViewer.next')}
                    </Button>
                </div>
            </div>

            <div style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                overflow: 'hidden'
            }}>
                <Document
                    file={fileData}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={(error) => {
                        setFileType('error');
                        setError(t('fileViewer.pdfLoadError') as string);
                        console.error('PDF load error:', error);
                    }}
                >
                    <Page
                        pageNumber={pageNumber}
                        width={window.innerWidth > 1200 ? 1200 : window.innerWidth - 40}
                        renderAnnotationLayer={false}
                        renderTextLayer={false}
                    />
                </Document>
            </div>
        </div>
    );

    const renderLoadingView = () => <CircularProgress sx={{margin: '40px auto'}}/>;

    if (fileData === null && textContent === null) {
        return renderLoadingView();
    } else {
        switch (fileType) {
            case 'error':
                return renderErrorView();
            case 'txt':
                return renderTextView();
            case 'unsupported':
                return renderUnsupportedView();
            case 'xlsx':
                return renderXlsxView();
            case 'docx':
                return renderDocxView();
            case 'pptx':
                return renderPptxView();
            case 'pdf':
                return renderPdfView();
            case 'loading':
            default:
                return renderLoadingView();
        }
    }
};

export default FileViewer;
